import { useEffect, useMemo, useState, type KeyboardEventHandler, type MouseEvent } from 'react'
import { searchProSkills, type ProSkillOption } from '../../api/pros'

type Props = {
  value: string[]
  onChange: (next: string[]) => void
  disabled?: boolean
  maxTags?: number
  placeholder?: string
}

function normalizeToken(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ')
}

export default function SkillsTagInput({
  value,
  onChange,
  disabled = false,
  maxTags = 20,
  placeholder = 'Type a skill and press Enter',
}: Props) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<ProSkillOption[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  const normalizedSet = useMemo(
    () => new Set(value.map((v) => normalizeToken(v).toLowerCase())),
    [value],
  )

  useEffect(() => {
    if (!query.trim()) {
      setResults([])
      return
    }
    let cancelled = false
    const timer = window.setTimeout(async () => {
      try {
        setLoading(true)
        const items = await searchProSkills(query.trim(), 20)
        if (!cancelled) setResults(items)
      } catch {
        if (!cancelled) setResults([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, 150)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [query])

  const addMany = (raw: string) => {
    if (disabled) return
    const incoming = raw
      .split(',')
      .map((token) => normalizeToken(token))
      .filter(Boolean)
    if (!incoming.length) return
    if (value.length >= maxTags) return
    const seen = new Set(value.map((token) => normalizeToken(token).toLowerCase()))
    const next = [...value]
    for (const token of incoming) {
      if (next.length >= maxTags) break
      const lower = token.toLowerCase()
      if (seen.has(lower)) continue
      seen.add(lower)
      next.push(token)
    }
    if (next.length === value.length) {
      setQuery('')
      return
    }
    onChange(next)
    setQuery('')
    setResults([])
    setOpen(false)
  }

  const removeSkill = (idx: number) => {
    if (disabled) return
    onChange(value.filter((_, i) => i !== idx))
  }

  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addMany(query)
      return
    }
    if (e.key === 'Backspace' && !query && value.length > 0) {
      removeSkill(value.length - 1)
    }
  }

  const onSelectSuggestion = (raw: string) => (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    addMany(raw)
  }

  return (
    <div className="relative mt-1">
      <div className="flex min-h-[42px] w-full flex-wrap items-center gap-2 rounded-lg border border-white/15 bg-black/30 px-2 py-2">
        {value.map((skill, idx) => (
          <span
            key={`${skill}-${idx}`}
            className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-xs text-white/85"
          >
            {skill}
            <button
              type="button"
              onClick={() => removeSkill(idx)}
              className="text-white/60 hover:text-white"
              disabled={disabled}
              aria-label={`Remove ${skill}`}
            >
              x
            </button>
          </span>
        ))}
        <input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
          }}
          onPaste={(e) => {
            const raw = e.clipboardData?.getData('text') || ''
            if (!raw.includes(',')) return
            e.preventDefault()
            addMany(raw)
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            window.setTimeout(() => setOpen(false), 120)
          }}
          onKeyDown={onKeyDown}
          disabled={disabled || value.length >= maxTags}
          className="min-w-[180px] flex-1 bg-transparent px-1 py-1 text-sm outline-none placeholder:text-white/45"
          placeholder={value.length >= maxTags ? `Max ${maxTags} skills` : placeholder}
        />
      </div>

      {open && (query.trim() || loading || results.length > 0) ? (
        <div className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-white/15 bg-black shadow-lg">
          {loading ? (
            <div className="px-3 py-2 text-xs text-white/65">Searching skills...</div>
          ) : results.length > 0 ? (
            <div className="py-1">
              {results.map((item) => {
                const exists = normalizedSet.has(item.name.toLowerCase())
                return (
                  <button
                    key={item.id}
                    type="button"
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-white/85 hover:bg-white/10"
                    onMouseDown={onSelectSuggestion(item.name)}
                    disabled={exists}
                  >
                    <span>{item.name}</span>
                    {exists ? <span className="text-xs text-white/40">Added</span> : null}
                  </button>
                )
              })}
            </div>
          ) : query.trim() ? (
            <button
              type="button"
              className="w-full px-3 py-2 text-left text-sm text-white/80 hover:bg-white/10"
              onMouseDown={onSelectSuggestion(query)}
            >
              Add "{normalizeToken(query)}"
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
